import { storedCartToken } from '@vue-storefront/core/modules/cart/helpers/cartTokenCookie'
import Vue from 'vue'
import i18n from '@vue-storefront/i18n'
import config from 'config'
import VueOfflineMixin from 'vue-offline/mixin'
import { mapGetters, mapActions } from 'vuex'

import Composite from '@vue-storefront/core/mixins/composite'
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { isServer } from '@vue-storefront/core/helpers'
import { Logger } from '@vue-storefront/core/lib/logger'
import { processURLAddress, timezoneTime, partnerWorkingTime } from '@vue-storefront/core/helpers'

import moment from 'moment'
import momentTime from 'moment-timezone'
import { notifyPaymentBeingChecked, notifyOrderAlreadyPaid } from 'src/modules/ipay-payment/helpers/paymentMessages'
import { otpVerificationOutcome, runOtpVerification } from 'src/modules/ipay-payment/helpers/otpVerification'
import { paymentCreateOutcome, ensurePaymentRecorded, unrecordedPaymentOutcome } from 'src/modules/ipay-payment/helpers/paymentRecording'
import { handleOrderProgress } from '@vue-storefront/core/modules/order/helpers/orderProgress'

export default {
  name: 'Checkout',
  mixins: [Composite, VueOfflineMixin],
  data () {
    return {
      cutleryQty: [],
      stockCheckCompleted: false,
      stockCheckOK: false,
      comments: [],
      confirmation: null, // order confirmation from server
      order: {},
      personalDetails: {},
      shipping: {},
      shippingMethods: [],
      deliveryTime: [],
      payment: {},
      orderReview: {},
      cartSummary: {},
      contactToReplace: false,
      validationResults: {
        personalDetails: { $invalid: true },
        shipping: { $invalid: true },
        payment: { $invalid: true }
      },
      focusedField: null,
      locationItems: [],
      clientAllowedRendering: false,
      otpPaymentToken: null,
      otpVerifying: false,
      confirmedOrderId: null,
      additional_orders: [],
      cartId: '',
      onlinePaymentData: null,
      _3DSVerification: false
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'user/getCurrentUser',
      isVirtualCart: 'cart/isVirtualCart',
      isThankYouPage: 'checkout/isThankYouPage',
      getGroupedByPartnerProducts: 'partners/getGroupedByPartnerProducts',
      showOtpModal: 'ipay-payment-user/showOtpModal',
      defaultCard: 'ipay-payment-user/getDefaultCreditCard',
      getCustomerIp: 'user/getCustomerIp',
      getCustomerUA: 'user/getCustomerUA',
      getCustomerAccept: 'user/getCustomerAccept',
      getPhoneNumber: 'user/getPhoneNumber',
      guestAddresses: 'address/getGuestAddresses',
      getTotalSubtotalPrice: 'cart/getTotalSubtotalPrice',
      getTotalDeliveryPrice: 'cart/getTotalDeliveryPrice'
    }),
    isNotPickup () {
      if (this.shippingMethods && this.shippingMethods.length) {
        return this.shippingMethods.find(method => (!!method && 'method' in method && method.method.method_code !== 'pickup'));
      }
      return true;
    },
    currentLocationCode () {
      return this.getGroupedByPartnerProducts.length ? this.getGroupedByPartnerProducts[0].partner.location_code : 'ternopil';
    },
    getCustomerId () {
      return this.$store.state.user.current ? this.$store.state.user.current.id : null
    },
    baseUrl () {
      return isServer ? 'https://www.korzyna.com' : window.location.origin
    },
    requestorUrl () {
      return isServer ? 'https://www.korzyna.com' : window.location.href
    },
    browserLanguage () {
      return isServer ? 'uk-UA' : window.navigator.language
    },
    browserDimensions () {
      return isServer ? { width: 500, height: 500 } : { width: window.innerWidth, height: window.innerHeight }
    },
    renderCheckout () {
      if (!isServer) {
        let t = setTimeout(() => {
          this.clientAllowedRendering = true
          clearTimeout(t)
        }, 50)
      }
      return !isServer && this.clientAllowedRendering
    },
    showThankYouPage () {
      return !this.showOtpModal && this.isThankYouPage
    },
    cardName () {
      return this.defaultCard ? this.defaultCard.name : null
    }
  },
  async beforeMount () {
    this.$nextTick(() => {
      this.cartId = storedCartToken()
    })
    this.$store.dispatch('checkout/setModifiedAt', Date.now())
    this.$bus.$on('user-after-loggedin-with-cart', this.processPartners)
    // TO-DO: Use one event with name as apram
    this.$bus.$on('user-after-loggedin', this.onUserLoggedIn)
    this.$bus.$on('cart-after-update', this.onCartAfterUpdate)
    this.$bus.$on('cart-after-delete', this.onCartAfterUpdate)
    this.$bus.$on('checkout-after-comment', this.onAfterComment)
    this.$bus.$on('checkout-after-cutleryQty', this.onAfterCutleryQty)
    this.$bus.$on('checkout-after-personalDetails', this.onAfterPersonalDetails)
    this.$bus.$on('checkout-after-shippingDetails', this.onAfterShippingDetails)
    this.$bus.$on('address-list-address-changed', this.onAfterAddressChanged)
    this.$bus.$on('checkout-after-deliveryTimeDetails', this.onAfterDeliveryTimeDetails)
    this.$bus.$on('checkout-after-paymentDetails', this.onAfterPaymentDetails)
    this.$bus.$on('checkout-after-cartSummary', this.onAfterCartSummary)
    this.$bus.$on('checkout-contact-to-replace-changed', this.onAfterContactToReplaceChanged)
    this.$bus.$on('checkout-no-product-changed', this.onAfterNoProductChanged)
    this.$bus.$on('gpay-token-received', this.onPayTokenReceived)
    this.$bus.$on('checkout-before-placeOrder', this.onBeforePlaceOrder)
    this.$bus.$on('checkout-do-placeOrder', this.onDoPlaceOrder)
    this.$bus.$on('checkout-before-edit', this.onBeforeEdit)
    this.$bus.$on('order-after-placed', this.onAfterPlaceOrder)
    this.$bus.$on('checkout-before-shippingMethods', this.onBeforeShippingMethods)
    this.$bus.$on('checkout-after-validationError', this.focusField)
    this.$bus.$on('otp-verification-submit', this.onOtpVerificationSubmitAfter)
    if (!this.showThankYouPage) {
      if (!isServer && !this.currentUser) {
        const guestAddressStorageData = localStorage.getItem('shop/user/guest-addresses');

        if (guestAddressStorageData) {
          const guestAddresses = JSON.parse(guestAddressStorageData);
          let guestAddress = null;

          if (Array.isArray(guestAddresses) && guestAddresses.length) {
            guestAddress = guestAddresses[0];
            await this.$store.dispatch('address/addQuoteAddress', guestAddress);
          }
        }
      }
      this.$store.dispatch('cart/load', { forceClientState: true }).then(() => {
        if (this.$store.state.cart.groupedCart.length === 0) {
          this.notifyEmptyCart()
          this.$router.push(this.localizedRoute('/'))
        } else {
          this.processPartners()
          this.stockCheckCompleted = false
          const checkPromises = []
          for (let product of this.$store.state.cart.cartItems) { // check the results of online stock check
            if (product.onlineStockCheckid) {
              checkPromises.push(new Promise((resolve, reject) => {
                Vue.prototype.$db.syncTaskCollection.getItem(product.onlineStockCheckid, (err, item) => {
                  if (err || !item) {
                    if (err) Logger.error(err)()
                    resolve(null)
                  } else {
                    if (!item.result) {
                      resolve(null)
                    } else {
                      product.stock = item.result
                      resolve(product)
                    }
                  }
                })
              }))
            }
          }
          Promise.all(checkPromises).then((checkedProducts) => {
            this.stockCheckCompleted = true
            this.stockCheckOK = true
            for (let chp of checkedProducts) {
              if (chp && chp.stock) {
                if (!chp.stock.is_in_stock) {
                  this.stockCheckOK = false
                  chp.errors.stock = i18n.t('Out of stock!')
                  this.notifyOutStock(chp)
                }
              }
            }
          })
        }
      })
    }
  },
  beforeDestroy () {
    this.$store.dispatch('checkout/setModifiedAt', 0) // exit checkout
    this.$bus.$off('user-after-loggedin-with-cart', this.processPartners)
    this.$bus.$off('user-after-loggedin', this.onUserLoggedIn)
    this.$bus.$off('cart-after-update', this.onCartAfterUpdate)
    this.$bus.$off('cart-after-delete', this.onCartAfterUpdate)
    this.$bus.$off('checkout-after-comment', this.onAfterComment)
    this.$bus.$off('checkout-after-cutleryQty', this.onAfterCutleryQty)
    this.$bus.$off('checkout-after-personalDetails', this.onAfterPersonalDetails)
    this.$bus.$off('checkout-after-shippingDetails', this.onAfterShippingDetails)
    this.$bus.$off('address-list-address-changed', this.onAfterAddressChanged)
    this.$bus.$off('checkout-after-deliveryTimeDetails', this.onAfterDeliveryTimeDetails)
    this.$bus.$off('checkout-after-paymentDetails', this.onAfterPaymentDetails)
    this.$bus.$off('checkout-after-cartSummary', this.onAfterCartSummary)
    this.$bus.$off('checkout-contact-to-replace-changed', this.onAfterContactToReplaceChanged)
    this.$bus.$off('checkout-no-product-changed', this.onAfterNoProductChanged)
    this.$bus.$off('gpay-token-received', this.onPayTokenReceived)
    this.$bus.$off('checkout-before-placeOrder', this.onBeforePlaceOrder)
    this.$bus.$off('checkout-do-placeOrder', this.onDoPlaceOrder)
    this.$bus.$off('checkout-before-edit', this.onBeforeEdit)
    this.$bus.$off('order-after-placed', this.onAfterPlaceOrder)
    this.$bus.$off('checkout-before-shippingMethods', this.onBeforeShippingMethods)
    this.$bus.$off('checkout-after-validationError', this.focusField)
    this.$bus.$off('otp-verification-submit', this.onOtpVerificationSubmitAfter)
  },
  watch: {
    'OnlineOnly': 'onNetworkStatusCheck',
    'orderComment': 'orderComment'
  },
  methods: {
    ...mapActions({
      saveDeliveryTime: 'checkout/saveDeliveryTime',
      createPayment: 'ipay-payment-user/createPayment',
      createTransaction: 'ipay-payment-user/createTransaction',
      failedTransaction: 'ipay-payment-user/failedTransaction',
      verify3DS: 'ipay-payment-user/verify3DS',
      toggleOtpModal: 'ipay-payment-user/toggleOtpModal',
      verify2DS: 'ipay-payment-user/verify2DS',
      setPaymentFailedPage: 'checkout/setPaymentFailedPage',
      getDeliveryTime: 'checkout/getDeliveryTime',
      clearCheckoutDetails: 'checkout/clearCheckoutDetails',
      updateShippingDetailsState: 'shipping/updateShippingDetailsState'
    }),
    onUserLoggedIn () {
      this.processPartners()
    },
    processPartners () {
      let deliveryTime = []
      let deliveryTimeSection = []
      this.getGroupedByPartnerProducts.forEach(item => {
        let partnerShipping = null
        if (item.partner.shipping.delivery_available) {
          partnerShipping = item.partner.shipping.methods.find(method => method.method_code === 'delivery')
        } else if (item.partner.shipping.pickup_available) {
          partnerShipping = item.partner.shipping.methods.find(method => method.method_code === 'pickup')
        } else {
          partnerShipping = item.partner.shipping.methods[0]
        }
        this.comments.push({
          partner: item.partner.code,
          comment: ''
        })
        this.cutleryQty.push({
          partner: item.partner.code,
          qty: 1
        })
        let partnerShippingMethod = this.shippingMethods.find(method => method.partner === item.partner.code)
        if (!partnerShippingMethod) {
          this.shippingMethods.push({
            partner: item.partner.code,
            method: partnerShipping
          })
        }

        deliveryTime.push({
          partner: item.partner.code,
          datetime: '',
          asap: item.partner.shipping.asap_available,
          replaceOutOfStock: 'call'
        })
        deliveryTimeSection.push({
          partner: item.partner.code,
          active: false
        })
        this.locationItems.push(...item.items)
      })
      this.deliveryTime = deliveryTime
      this.saveDeliveryTime(this.deliveryTime)
      this.$bus.$emit('update-delivery-time')
    },
    onCartAfterUpdate (payload) {
      if (this.$store.state.cart.groupedCart.length === 0) {
        this.notifyEmptyCart()
        this.$router.push(this.localizedRoute('/'))
      }
    },
    onBeforeShippingMethods (country) {
      this.$forceUpdate()
    },
    onAfterContactToReplaceChanged (value) {
      this.contactToReplace = value
      localStorage.setItem('shop/checkoutFieldValues/contact-to-replace', value);
    },
    onAfterNoProductChanged ({partner, value}) {
      let deliveryTime = this.deliveryTime.find(code => code.partner === partner)
      deliveryTime.replaceOutOfStock = value
    },
    onPayTokenReceived (token) {
      this.onlinePaymentData = token
    },
    onAfterPlaceOrder (payload) {
      // K2-3073: the order's loader stays on through the payment and stops on every exit.
      return handleOrderProgress(this.$bus, payload, () => this.finishPlacedOrder(payload), () => Boolean(this._3DSVerification))
    },
    async finishPlacedOrder (payload) {
      this.confirmation = payload.confirmation
      if (this.$store.state.checkout.personalDetails.createAccount) {
        await this.$store.dispatch('user/login', {
          email: this.$store.state.checkout.personalDetails.emailAddress,
          password: this.$store.state.checkout.personalDetails.password
        })
      }
      /* let orderTag = 'Order ID: ' + payload.confirmation.orderNumber;
      let isLoggedIn = this.$store.state.user.current !== null
      if (!isServer && Object.keys(window.hj).length > 0) {
        window.hj('tagRecording', [orderTag]);
        if (isLoggedIn) {
          window.hj('tagRecording', ['Logged In(After place order)']);
        } else {
          window.hj('tagRecording', ['Guest User(After place order)']);
        }
      } */
      delete this.payment.banknoteDenomination
      localStorage.removeItem('shop/checkoutFieldValues/comment')
      localStorage.removeItem('shop/checkoutFieldValues/contact-to-replace')
      let lastOrderConfirm = this.$store.state.order.last_order_confirmation
        ? this.$store.state.order.last_order_confirmation.confirmation
        : false
      let additionalOrders = this.confirmation.orders.filter(o => o.id !== lastOrderConfirm.backendOrderId)
      if (additionalOrders.length) {
        this.additional_orders = additionalOrders.map(o => o.id)
      }
      if (lastOrderConfirm && ((this.cardName && this.payment.paymentMethod === 'ipay_masterpass') || ['ipay_applepay', 'ipay_gpay'].includes(this.payment.paymentMethod))) {
        this.$bus.$emit('notification-progress-start', this.$t('Processing payments ...'))
        // we use server results after order placing to avoid any manipulations with totals on client side
        let grandTotal = 0
        lastOrderConfirm.orders.forEach(order => {
          grandTotal += order.grand_total
        })
        let amount = parseInt(grandTotal.toFixed(2) * 100)
        let onlinePaymentPayload = {
          // K2-3073: vuestorefront-api charges what ms-api says these orders owe.
          order_ids: this.confirmation.orders.map(order => order.id),
          cardName: this.cardName,
          customerId: this.getCustomerId,
          phoneNumber: this.getPhoneNumber(true),
          amount,
          description: `Оплата замовлення №${lastOrderConfirm.magentoOrderId} на korzyna.com`,
          info: {},
          ext_id: lastOrderConfirm.magentoOrderId,
          threeds_info: {
            notification_url: `${this.baseUrl}${config.ipay.payment.verify_threeds_endpoint}?order_id=${lastOrderConfirm.backendOrderId}&additional_orders=${this.additional_orders}`,
            threeds_requestor_url: this.requestorUrl,
            browser_language: this.browserLanguage,
            browser_screen_height: this.browserDimensions.height,
            browser_screen_width: this.browserDimensions.width,
            browser_accept_header: this.getCustomerAccept,
            browser_tz: new Date().getTimezoneOffset(),
            browser_user_agent: this.getCustomerUA,
            user_ip: this.getCustomerIp
          }
        }
        onlinePaymentPayload.method = this.payment.paymentMethod.replace('ipay_', '')
        if (['ipay_gpay', 'ipay_applepay'].includes(this.payment.paymentMethod)) {
          onlinePaymentPayload.token = this.onlinePaymentData
        }
        await this.createPayment(onlinePaymentPayload)
          .then(async res => {
            const created = paymentCreateOutcome(res)
            if (created === 'paid') {
              // K2-3073: already paid (by an earlier payment) - never a failure.
              notifyOrderAlreadyPaid(this.$store, key => i18n.t(key))
              return null
            }
            if (created === 'pending') {
              // K2-3073: iPay may have the payment (no answer, a payment in
              // progress or being checked) - never reported as failed.
              this.notifyPaymentBeingChecked()
              return null
            }
            // Also 401/404/409/422/502 now that vuestorefront-api checks the order and token.
            if (created === 'failed') {
              console.error(res)
              this.setPaymentFailedPage(res || {})
              this.failedTransaction({ order_id: lastOrderConfirm.backendOrderId, additional_orders: this.additional_orders })
              this.$router.push('/checkout/payment/failed')
              throw new Error('Something went wrong')
            }
            // Recorded before a 3DS redirect leaves the page - the bank callback needs them.
            // vuestorefront-api records it itself; create-transaction is the fallback.
            const recorded = await ensurePaymentRecorded(res, this.confirmation.orders, order => this.createTransaction({
              order_id: order.id,
              customer_id: this.getCustomerId,
              transaction_id: res.result.pmt_id,
              type: this.payment.paymentMethod,
              transaction_data: res.result,
              multiple: this.confirmation.orders.length > 1
            }))
            if (!recorded) {
              if (unrecordedPaymentOutcome(res.result) === 'pending') {
                // Possibly charged: never reported as failed.
                this.notifyPaymentBeingChecked()
                return null
              }
              this.setPaymentFailedPage({ message: i18n.t('Something went wrong. Try again in a few seconds.') })
              this.$router.push('/checkout/payment/failed')
              throw new Error('The payment was not recorded')
            }
            return res
          })
          .then((res) => {
            if (!res) {
              return res
            }
            let result = res.result
            if (parseInt(result.pmt_status) === 5) {
              return res
            }
            if (result.security_rate === '3D' && result.security_data && result.security_data.redirect_url) {
              // The loader stays on while the bank's page loads.
              this._3DSVerification = true
              window.location.href = result.security_data.redirect_url
              return
            }
            this.$bus.$emit('notification-progress-stop', {})
            this.otpPaymentToken = result.security_data ? result.security_data.token : result.token
            this.confirmedOrderId = lastOrderConfirm.backendOrderId
            this.toggleOtpModal()
            this.$bus.$emit('modal-show', 'otp-verification')
            return res
          })
      }
      // clear store after placing order
      if (!this.currentUser) {
        try {
          await this.clearGuestDataAfterPlaceOrder();
        } catch (error) {
          Logger.error('Error by try to clear checkout:', error)();
        }
      }

      if (!this._3DSVerification) {
        await this.$store.dispatch('checkout/setThankYouPage', true)
      }

      Logger.debug(payload.order)()
    },
    onBeforeEdit (section) {
    },
    onBeforePlaceOrder (payload) {},
    onAfterCartSummary (receivedData) {
      this.cartSummary = receivedData
    },
    onDoPlaceOrder (additionalPayload) {
      if (this.$store.state.cart.groupedCart.length === 0) {
        this.notifyEmptyCart()
        this.$router.push(this.localizedRoute('/'))
      } else {
        this.payment.paymentMethodAdditional = additionalPayload
        this.placeOrder()
      }
    },

    onAfterComment (commentText, partner) {
      let comment = this.comments.find(c => c.partner === partner)
      if (!comment) {
        // Same mount-time race as onAfterCutleryQty: Comment.vue can restore
        // a persisted comment and emit before processPartners() has
        // populated an entry for this partner yet.
        this.comments.push({ partner, comment: commentText })
      } else {
        comment.comment = commentText
      }
      this.saveComments()
    },

    onAfterCutleryQty (qty, partner) {
      let cutleryQty = this.cutleryQty.find(c => c.partner === partner)
      if (!cutleryQty) {
        // CutleryQty.vue can restore a persisted qty and emit this on mount,
        // before processPartners() (async, inside cart/load().then()) has
        // populated an entry for this partner yet - create it instead of
        // dropping the customer's saved cutlery choice.
        this.cutleryQty.push({ partner, qty })
      } else {
        cutleryQty.qty = qty
      }
      this.saveCutleryQty()
    },

    onAfterPaymentDetails (receivedData) {
      this.payment = receivedData
      this.savePaymentDetails()
    },
    onAfterShippingDetails (receivedData, validationResult) {
      this.shipping = receivedData
      this.validationResults.shipping = validationResult
      this.saveShippingDetails()

      const storeView = currentStoreView()
      storeView.tax.defaultCountry = this.shipping.country
    },
    async onAfterAddressChanged (newAddress) {
      await this.$store.dispatch('cart/load', { refreshTotals: true, isUpdate: true })
    },
    onAfterDeliveryTimeDetails (datetime, validationResult, type, partner, shippingMethod, nextPartnerCode = false) {
      let deliveryTime = this.deliveryTime.find(code => code.partner === partner)
      if (!deliveryTime) {
        return
      }
      if (type === 'asap') {
        deliveryTime.asap = true
        deliveryTime.datetime = ''
      } else {
        deliveryTime.asap = false
        deliveryTime.datetime = datetime
      }
      deliveryTime.shippingMethod = shippingMethod.carrier_code
      this.validationResults.deliveryTime = validationResult
      this.saveDeliveryTime()
      this.shippingMethods.find(m => m.partner === partner).method = shippingMethod
      if (!nextPartnerCode) {
        const storeView = currentStoreView()
        let country = this.$store.state.checkout.shippingDetails.country
        if (!country) country = storeView.i18n.defaultCountry
        this.$bus.$emit('checkout-before-shippingMethods', country)
      }
    },
    onAfterPersonalDetails (receivedData, validationResult) {
      this.personalDetails = receivedData
      this.validationResults.personalDetails = validationResult

      this.savePersonalDetails()
      this.focusedField = null
    },
    onNetworkStatusCheck (isOnline) {
      this.checkConnection(isOnline)
    },
    onOtpVerificationSubmitAfter ({ value }) {
      // K2-3073: loader while the code is checked; a repeated submit is ignored.
      return runOtpVerification(this, this.$t('Processing payments ...'), () => this.verify2DS({
        customerId: this.getCustomerId,
        phoneNumber: this.getPhoneNumber(true),
        token: this.otpPaymentToken,
        value,
        order_id: this.confirmedOrderId,
        additional_orders: this.additional_orders
      })
        .then(resp => {
          const outcome = otpVerificationOutcome(resp)
          if (outcome === 'pending') {
            // Possibly charged (202): never reported or shown as failed.
            this.notifyPaymentBeingChecked()
            return resp
          }
          if (outcome === 'wrong_code' || (outcome === 'failed' && resp.code === 500)) {
            console.error(resp)
            this.setPaymentFailedPage({ message: 'Невірно вказано код підтвердження' })
            this.failedTransaction({ order_id: this.confirmedOrderId })
            this.$router.push('/checkout/payment/failed')
          } else if (outcome === 'failed') {
            console.error(resp)
            this.setPaymentFailedPage(resp)
            this.failedTransaction({ order_id: this.confirmedOrderId })
            this.$router.push('/checkout/payment/failed')
          }
          return resp
        })
        .catch(err => {
          // No answer: the confirmation may have gone through.
          console.error(err)
          this.notifyPaymentBeingChecked()
        })
        .finally(() => this.toggleOtpModal()))
    },
    notifyPaymentBeingChecked () {
      notifyPaymentBeingChecked(this.$store, key => i18n.t(key))
    },
    refreshDeliveryTimeRanges (child) {
      this.getDeliveryTime({
        partner_code: child.group.partner.code,
        location_code: child.group.partner.location_code,
        shipping_method: child.shippingMethod,
        cartId: this.cartId
      })
        .then((response) => {
          child.dateTimeRanges = response;
          let dateExists = false;
          if (child.deliveryTime.datetime) {
            dateExists = child.checkDatetimeExisting(child.deliveryTime.datetime)
          }
          if (!dateExists) {
            child.activeItem = child.getActiveDay(child.dateTimeRanges)
            child.setDeliveryTime(child.activeItem, child.getActiveRange);
            child.selectedDateTime = child.activeItem + ' ' + child.getActiveRange;
          }
          child.$forceUpdate();
        });
    },
    checkStocks () {
      if (this.currentUser && !this.currentUser.phone_verified) {
        let personalDetailsComponent = this.$children[0]
        personalDetailsComponent.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return { status: false, message: 'Phone is not verified!' }
      }
      let isValid = true
      let scrolled = false
      for (let child of this.$children) {
        if (typeof child.$v !== 'undefined' && child.$v.$invalid && !scrolled) {
          child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          scrolled = true;
        }
        if (child.hasOwnProperty('personalDetails')) {
          if (child.$v.personalDetails) {
            if (child.$v.personalDetails.$invalid || child.isNotValidPhone) {
              child.$v.personalDetails.$touch();
              child.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
              return {
                status: false,
                message: 'Some of the form fields are not valid!'
              }
            }
          }
        }
        if (child.hasOwnProperty('myAddressDetails')) {
          if (child.currentUser && (!child.shipping.api_details || !child.shipping.longitude || !child.shipping.latitude)) {
            this.$bus.$emit('set-has-error-on-address-field', true);
            child.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
            return {
              status: false,
              message: this.$t('Address is not valid!')
            };
          } else if (!child.currentUser && child.$children[0] && child.$children[0].guestAddress) {
            const address = child.$children[0].guestAddress;
            if (!address.api_details || !address.longitude || !address.latitude) {
              this.$bus.$emit('set-has-error-on-address-field', true);
              child.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
              return {
                status: false,
                message: this.$t('Address is not valid!')
              };
            }
          }

          if (this.$store.state.shipping.shippingDetailsState.showNewAddressForm) {
            this.$bus.$emit('set-has-error-on-address-field', true);
            child.$el.scrollIntoView({behavior: 'smooth', block: 'center'});
            return {
              status: false,
              message: this.$t('Please confirm your address before continuing')
            };
          }

          this.$bus.$emit('set-has-error-on-address-field', false);
        }
        if (child.hasOwnProperty('deliveryTime')) {
          if (!child.deliveryTime.asap) {
            if (child.deliveryTime.datetime === '' && !scrolled) {
              this.refreshDeliveryTimeRanges(child);
              child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              return {
                status: 'incDate',
                partner: child.group.partner.name
              };
            }

            let timeRange = child.deliveryTime.datetime.split(' ');

            let startTime = new Date(timeRange[0] + ' ' + timeRange[1]);
            startTime = moment(startTime).subtract(30, 'm').toDate();
            let currentDate = timezoneTime();

            if (startTime < currentDate && !scrolled) {
              this.refreshDeliveryTimeRanges(child);
              child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              child.selectedDateTime = '';
              child.show = '';
              return {
                status: 'incDate',
                partner: child.group.partner.name
              };
            }
          } else {
            let currentDate = timezoneTime()
            let workingTime = partnerWorkingTime(child.group.partner)
            let workingFrom = new Date(currentDate.getFullYear() + '/' + (currentDate.getMonth() + 1) + '/' + currentDate.getDate() + ' ' + workingTime.workFrom)
            let workingTo = new Date(currentDate.getFullYear() + '/' + (currentDate.getMonth() + 1) + '/' + currentDate.getDate() + ' ' + workingTime.workTo)
            if (currentDate < workingFrom || currentDate > workingTo) {
              child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              this.$bus.$emit('hideAsapButton', child.group.partner)

              return {
                status: 'asapDate',
                partner: child.group.partner.name
              };
            }
          }
        }
        if (child.hasOwnProperty('$v')) {
          if (child.$v.$invalid) {
            child.$v.$touch();
            isValid = false;
            child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return {
              status: false,
              message: 'Some of the form fields are not valid!'
            }
          }
        }
        if (typeof child.isValid !== 'undefined' && !child.isValid) {
          if (typeof child.validate !== 'undefined') child.validate();

          isValid = false;
          child.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });

          if (child.$options.name === 'Shipping' && child.shippingDetailsState.showNewAddressForm) {
            return {
              status: false,
              message: 'Please confirm your address before continuing'
            }
          }

          return {
            status: false,
            message: 'Some of the form fields are not valid!'
          }
        }
      }

      if (typeof navigator !== 'undefined' && navigator.onLine) {
        if (this.stockCheckCompleted) {
          if (!this.stockCheckOK) {
            isValid = false
            this.notifyNotAvailable()
          }
        } else {
          this.notifyStockCheck()
          isValid = false
        }
      }
      return { status: isValid }
    },
    checkConnection (isOnline) {
      if (!isOnline) {
        this.notifyNoConnection()
      }
    },
    // This method checks if there exists a mapping of chosen payment method to one of Magento's payment methods.
    getPaymentMethod () {
      let paymentMethod = this.payment.paymentMethod
      if (config.orders.payment_methods_mapping.hasOwnProperty(paymentMethod)) {
        paymentMethod = config.orders.payment_methods_mapping[paymentMethod]
      }
      return paymentMethod
    },
    prepareOrder () {
      this.order = {
        user_id: this.$store.state.user.current ? this.$store.state.user.current.id.toString() : '',
        quote_id: this.$store.state.cart.cartServerToken ? this.$store.state.cart.cartServerToken : '',
        comments: this.comments,
        cutlery_qty: this.cutleryQty,
        items: this.locationItems,
        partner_code: 'store',
        location_code: this.currentLocationCode,
        shippingMethods: this.shippingMethods,
        deliveryTime: this.deliveryTime,
        contactToReplace: this.contactToReplace,
        addressInformation: {
          banknote_denomination: this.getPaymentMethod() === 'cash' ? this.payment.banknoteDenomination : null,
          billingAddress: {
            state: this.shipping.state,
            country: this.shipping.country,
            street: this.isNotPickup ? this.shipping.street : '',
            house: this.isNotPickup ? this.shipping.house : '',
            apartment: this.isNotPickup ? this.shipping.apartment : '',
            company: this.payment.company,
            phone_number: this.personalDetails.phoneNumber && this.personalDetails.phoneNumber !== '+38(' ? this.personalDetails.phoneNumber : this.shipping.phoneNumber,
            postcode: this.shipping.postcode ? this.shipping.postcode : this.shipping.zipCode,
            city: this.shipping.city,
            firstname: this.shipping.firstName ? this.shipping.firstName : this.personalDetails.firstName,
            lastname: this.shipping.lastName ? this.shipping.lastName : this.personalDetails.lastName,
            email: this.personalDetails.emailAddress,
            vat_id: this.payment.taxId,
            comment: this.shipping.comment ? this.shipping.comment : '',
            longitude: this.shipping.longitude ? this.shipping.longitude : '',
            latitude: this.shipping.latitude ? this.shipping.latitude : '',
            api_details: this.shipping.api_details ? this.shipping.api_details : '',
            add_new_address: this.$store.state.checkout.addNewAddress,
            default_shipping: this.shipping.default_shipping
          },
          payment_method_code: this.getPaymentMethod(),
          payment_method_additional: this.payment.paymentMethodAdditional,
          shippingExtraFields: this.shipping.extraFields
        }
      }
      if (!this.currentUser) {
        let guestAddress = this.guestAddresses[0] || {}
        this.order.addressInformation.billingAddress = {
          ...this.order.addressInformation.billingAddress,
          ...guestAddress
        }
      }
      return this.order
    },
    placeOrder () {
      this.checkConnection({ online: typeof navigator !== 'undefined' ? navigator.onLine : true })
      let valid = this.checkStocks()
      if (valid.status === 'incDate') {
        this.incorrectDeliveryTime(valid.partner)
        return
      }
      if (valid.status === 'asapDate') {
        this.incorrectAsapTime(valid.partner)
        return
      }
      if (valid.status) {
        this.$store.dispatch('checkout/placeOrder', { order: this.prepareOrder() })
        window.scrollTo(0, 0);
      } else {
        this.notifyNotValid(valid.message)
      }
    },

    saveComments () {
      this.$store.dispatch('checkout/setComment', this.comments)
    },

    saveCutleryQty () {
      this.$store.dispatch('checkout/saveCutleryQty', this.cutleryQty)
    },

    savePersonalDetails () {
      this.$store.dispatch('checkout/savePersonalDetails', this.personalDetails)
    },
    saveShippingDetails () {
      this.$store.dispatch('checkout/saveShippingDetails', this.shipping)
    },
    saveDeliveryTime () {
      this.$store.dispatch('checkout/saveDeliveryTime', this.deliveryTime)
    },
    savePaymentDetails () {
      this.$store.dispatch('checkout/savePaymentDetails', this.payment)
    },
    focusField (fieldName) {
      if (fieldName === 'password') {
        window.scrollTo(0, 0)
        this.focusedField = fieldName
      }
      if (fieldName === 'email-address') {
        window.scrollTo(0, 0)
        this.focusedField = fieldName
      }
    },

    async clearGuestDataAfterPlaceOrder () {
      await this.clearCheckoutDetails();
      await this.updateShippingDetailsState({
        shipToMyAddress: false,
        activeShippingAddressId: false,
        showNewAddressForm: false
      });
    }
  },
  metaInfo () {
    return {
      title: this.$route.meta.title || i18n.t('Checkout'),
      meta: this.$route.meta.description ? [{ vmid: 'description', name: 'description', content: this.$route.meta.description }] : []
    }
  },
  asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data
    return new Promise((resolve, reject) => {
      if (!context) {
        resolve();
        return;
      }
      context.output.cacheTags.add(`checkout`)
      if (!route.params.locationCode) {
        let redirectUrl = '/'
        if (store.state.cart.cartItems.length) {
          let firstItem = store.state.cart.cartItems[0]
          redirectUrl = firstItem && firstItem.location_code ? `/checkout/${firstItem.location_code}` : '/'
        }
        context.server.response.redirect(redirectUrl)
      }
      resolve()
    })
  }
}
